import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { getAdminToken } from "@/lib/admin/session";
import { listOffers, upsertOffer, deleteOffer } from "@/lib/admin/cms.functions";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/offerte/")({
  head: () => ({ meta: [{ title: "Offerte · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminOffers,
});

type Offer = {
  id: string;
  type: "popup";
  title: string;
  description: string | null;
  image: string | null;
  cta_text: string | null;
  cta_link: string | null;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
};

function empty(): Offer {
  return { id: "", type: "popup", title: "", description: "", image: null, cta_text: "", cta_link: "", starts_at: null, ends_at: null, active: true };
}

function AdminOffers() {
  const fetchList = useServerFn(listOffers);
  const save = useServerFn(upsertOffer);
  const remove = useServerFn(deleteOffer);
  const [items, setItems] = useState<Offer[]>([]);
  const [editing, setEditing] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try { const res = await fetchList({ data: { token } }); setItems(res.offers as unknown as Offer[]); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function handleSave() {
    if (!editing) return;
    const token = getAdminToken(); if (!token) return;
    if (!editing.title.trim() && !editing.description?.trim() && !editing.image) {
      toast.error("Aggiungi almeno un'immagine o un testo");
      return;
    }
    try {
      await save({
        data: {
          token, id: editing.id || null,
          offer: {
            type: editing.type,
            title: editing.title,
            description: editing.description || null,
            image: editing.image || null,
            cta_text: editing.cta_text || null,
            cta_link: editing.cta_link || null,
            starts_at: editing.starts_at || null,
            ends_at: editing.ends_at || null,
            active: editing.active,
          },
        },
      });
      toast.success("Offerta salvata");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  async function handleDelete(id: string) {
    if (!confirm("Eliminare?")) return;
    const token = getAdminToken(); if (!token) return;
    try { await remove({ data: { token, id } }); toast.success("Eliminata"); refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  return (
    <AdminShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h1 className="font-display text-3xl">Offerte</h1><p className="text-sm text-slate-600">Popup mostrato una volta per sessione</p></div>
          <Button onClick={() => setEditing(empty())} className="bg-brand-red hover:bg-brand-red-hover"><Plus className="h-4 w-4 mr-2" /> Nuova offerta</Button>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? <div className="p-8 text-center text-slate-500">Caricamento...</div>
          : items.length === 0 ? <div className="p-8 text-center text-slate-500">Nessuna offerta</div>
          : <div className="divide-y divide-slate-200">
              {items.map((o) => (
                <div key={o.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden shrink-0">{o.image && <img src={o.image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="font-medium truncate">{o.title}</span>
                      <span className="text-[10px] uppercase px-1.5 py-0.5 bg-slate-200 rounded">popup</span>
                      {!o.active && <span className="text-[10px] uppercase px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">inattiva</span>}
                    </div>
                    {o.description && <div className="text-xs text-slate-500 truncate">{o.description}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(o)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(o.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              ))}
            </div>}
        </div>

        <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetContent className="sm:max-w-xl w-full overflow-y-auto">
            <SheetHeader><SheetTitle>{editing?.id ? "Modifica offerta" : "Nuova offerta"}</SheetTitle></SheetHeader>
            {editing && (
              <div className="mt-6 space-y-4 pb-12">
                <div><Label>Titolo</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Facoltativo se usi solo un'immagine" /></div>
                <div><Label>Descrizione</Label><Textarea rows={3} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                <ImageUploader value={editing.image ? [editing.image] : []} onChange={(v) => setEditing({ ...editing, image: v[0] ?? null })} bucket="offer-images" label="Immagine" />
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Testo CTA</Label><Input value={editing.cta_text ?? ""} onChange={(e) => setEditing({ ...editing, cta_text: e.target.value })} /></div>
                  <div><Label>Link CTA</Label><Input value={editing.cta_link ?? ""} onChange={(e) => setEditing({ ...editing, cta_link: e.target.value })} placeholder="/prodotti" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Inizio</Label><Input type="datetime-local" value={editing.starts_at?.slice(0, 16) ?? ""} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
                  <div><Label>Fine</Label><Input type="datetime-local" value={editing.ends_at?.slice(0, 16) ?? ""} onChange={(e) => setEditing({ ...editing, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
                </div>
                <label className="flex items-center justify-between p-3 border rounded-md"><span className="text-sm">Attiva</span><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /></label>
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSave} className="bg-brand-red hover:bg-brand-red-hover">Salva</Button>
                  <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AdminShell>
  );
}